/**
 * Progress checklist for raza request show (Journey A ops path):
 * request → events → halls → sadarat → grant → calendar → lagat
 *
 * Events, halls, and lagat are optional — a request can be granted with none.
 */

export type RazaProgressStepId =
  | "request"
  | "events"
  | "halls"
  | "sadarat"
  | "grant"
  | "calendar"
  | "lagat";

/** `done` complete · `todo` action still needed · `na` not applicable yet / not required */
export type RazaProgressStepStatus = "done" | "todo" | "na";

export type RazaProgressStep = {
  id: RazaProgressStepId;
  label: string;
  status: RazaProgressStepStatus;
  /** Short hint under the label (todo / na / optional done context) */
  detail?: string;
  /** Events, halls, and lagat are optional for Journey B / light ops */
  optional?: boolean;
};

export type RazaProgressEventInput = {
  needSadaratMale?: boolean | null;
  needSadaratFemale?: boolean | null;
  sadaratMaleId?: string | null;
  sadaratFemaleId?: string | null;
  sadaratMaleName?: string | null;
  sadaratFemaleName?: string | null;
  /** Legacy single-slot */
  needSadarat?: boolean | null;
  sadaratName?: string | null;
  googleCalendarLinked?: boolean | null;
  hallCount?: number | null;
  /** Per-event lagat (`razaEventId` only — not always in request `lagatReceiptCount`) */
  lagatReceipt?: { id?: string | null } | null;
};

export type RazaProgressInput = {
  razaGranted?: string | Date | null;
  lagatReceiptCount?: number | null;
  booking?: {
    hallBookingCount?: number | null;
  } | null;
  razaEvents?: RazaProgressEventInput[] | null;
};

export type RazaProgressChecklist = {
  steps: RazaProgressStep[];
  /** Steps with status `done` (excludes `na`) */
  completedCount: number;
  /** Steps that are not `na` (required + optional open/complete) */
  applicableCount: number;
  /** `todo` steps that are not optional — blocks “ready” */
  blockingTodoCount: number;
};

function needsSadarat(ev: RazaProgressEventInput): boolean {
  if (ev.needSadaratMale !== undefined || ev.needSadaratFemale !== undefined) {
    return Boolean(ev.needSadaratMale) || Boolean(ev.needSadaratFemale);
  }
  return ev.needSadarat !== false;
}

function slotFilled(
  needed: boolean,
  id: string | null | undefined,
  name: string | null | undefined
): boolean {
  if (!needed) return true;
  if (id != null && String(id).trim() !== "") return true;
  return Boolean(name?.trim());
}

/** Complete only when every required gender slot is filled. */
function hasSadarat(ev: RazaProgressEventInput): boolean {
  if (ev.needSadaratMale !== undefined || ev.needSadaratFemale !== undefined) {
    return (
      slotFilled(Boolean(ev.needSadaratMale), ev.sadaratMaleId, ev.sadaratMaleName) &&
      slotFilled(Boolean(ev.needSadaratFemale), ev.sadaratFemaleId, ev.sadaratFemaleName)
    );
  }
  return Boolean(ev.sadaratName?.trim());
}

function hallSlotCount(input: RazaProgressInput, events: RazaProgressEventInput[]): number {
  const fromBooking =
    typeof input.booking?.hallBookingCount === "number" ? input.booking.hallBookingCount : 0;
  const fromEvents = events.reduce((sum, ev) => {
    const n = typeof ev.hallCount === "number" ? ev.hallCount : 0;
    return sum + Math.max(0, n);
  }, 0);
  return Math.max(fromBooking, fromEvents);
}

/** Build ordered progress steps from a hydrated raza request show record. */
export function buildRazaRequestProgressChecklist(input: RazaProgressInput): RazaProgressChecklist {
  const events = Array.isArray(input.razaEvents) ? input.razaEvents : [];
  const eventsNeedingSadarat = events.filter(needsSadarat);
  const sadaratAllocated = eventsNeedingSadarat.filter(hasSadarat).length;
  const calendarSynced = eventsNeedingSadarat.filter((ev) =>
    Boolean(ev.googleCalendarLinked)
  ).length;
  const halls = hallSlotCount(input, events);
  const requestLagatCount =
    typeof input.lagatReceiptCount === "number" && input.lagatReceiptCount > 0
      ? input.lagatReceiptCount
      : 0;
  const eventLagatCount = events.filter((ev) => Boolean(ev.lagatReceipt?.id)).length;
  const hasLagat = requestLagatCount > 0 || eventLagatCount > 0;
  const granted = Boolean(input.razaGranted);

  const steps: RazaProgressStep[] = [
    {
      id: "request",
      label: "Request",
      status: "done",
      detail: "Created",
    },
    {
      id: "events",
      label: "Events",
      optional: true,
      status: events.length > 0 ? "done" : "todo",
      detail: events.length > 0 ? undefined : "Optional — ceremony events",
    },
    {
      id: "halls",
      label: "Halls",
      optional: true,
      status: halls > 0 ? "done" : "todo",
      detail: halls > 0 ? undefined : "Optional — booking & hall slots",
    },
    (() => {
      if (events.length === 0) {
        return {
          id: "sadarat" as const,
          label: "Sadarat",
          status: "na" as const,
          detail: "After events exist",
        };
      }
      if (eventsNeedingSadarat.length === 0) {
        return {
          id: "sadarat" as const,
          label: "Sadarat",
          status: "na" as const,
          detail: "Not required for these events",
        };
      }
      if (sadaratAllocated >= eventsNeedingSadarat.length) {
        return {
          id: "sadarat" as const,
          label: "Sadarat",
          status: "done" as const,
        };
      }
      return {
        id: "sadarat" as const,
        label: "Sadarat",
        status: "todo" as const,
        detail: `${sadaratAllocated} of ${eventsNeedingSadarat.length} allocated`,
      };
    })(),
    {
      id: "grant",
      label: "Grant",
      status: granted ? "done" : "todo",
      detail: granted ? undefined : "Mark raza granted",
    },
    (() => {
      if (events.length === 0) {
        return {
          id: "calendar" as const,
          label: "Calendar",
          status: "na" as const,
          detail: "After events exist",
        };
      }
      if (eventsNeedingSadarat.length === 0) {
        return {
          id: "calendar" as const,
          label: "Calendar",
          status: "na" as const,
          detail: "No calendar sync (need sadarat off)",
        };
      }
      if (calendarSynced >= eventsNeedingSadarat.length) {
        return {
          id: "calendar" as const,
          label: "Calendar",
          status: "done" as const,
        };
      }
      return {
        id: "calendar" as const,
        label: "Calendar",
        status: "todo" as const,
        detail: `${calendarSynced} of ${eventsNeedingSadarat.length} synced`,
      };
    })(),
    {
      id: "lagat",
      label: "Lagat",
      optional: true,
      status: hasLagat ? "done" : "todo",
      detail: hasLagat ? undefined : "Optional — request or event lagat",
    },
  ];

  const applicable = steps.filter((s) => s.status !== "na");
  const completedCount = applicable.filter((s) => s.status === "done").length;
  const blockingTodoCount = applicable.filter((s) => s.status === "todo" && !s.optional).length;

  return {
    steps,
    completedCount,
    applicableCount: applicable.length,
    blockingTodoCount,
  };
}
